import { PLAIN_VIEW, isPlainView } from './plainViewHelper';
import { isBrowser } from './browserHelper';
import {
  NATIVE_APP_OPERATION,
  NATIVE_APP,
  NativeAppParsedQueryParams,
  NativeAppQueryParamKeys,
  getNativeAppQueryParamNames,
  getNativeAppQueryParams,
  isNativeAppView,
  isNativeAppOperation,
} from './nativeAppHelper';

export const OPERATION_SUB_PAGES = {
  CONFIRM: 'confirm',
  CONTACT: 'contact',
  SERVICES: 'services',
  TIME_SLOTS: 'timeslots',
} as const;

export type OperationPath =
  (typeof OPERATION_SUB_PAGES)[keyof typeof OPERATION_SUB_PAGES];

const BOKA_TEST_SERVICE_OPERATION = 'takingataxitothenewtestenvironment';
export const TIMESLOTSTODAY = 'timeslotstoday';

export const isBokaTestServiceURL = () => {
  if (!isBrowser()) return false;
  return window.location.pathname.includes(BOKA_TEST_SERVICE_OPERATION);
};

export const getBokaSubPathname = (
  pathname: string,
  subPath: typeof SUB_PATH,
): string => {
  let prodPath;
  if (isPlainView()) {
    prodPath = `/xx/yy/appointment${subPath}/${PLAIN_VIEW}/`;
  } else if (isNativeAppView()) {
    prodPath = `/xx/yy/appointment${subPath}/${NATIVE_APP}/`;
  } else {
    prodPath = `/xx/yy/appointment${subPath}/`;
  }

  let localPath;
  if (isPlainView()) {
    localPath = `/${PLAIN_VIEW}/`;
  } else if (isNativeAppView()) {
    localPath = `/${NATIVE_APP}/`;
  } else {
    localPath = '/';
  }
  const appBasePath = {
    PROD: prodPath,
    LOCAL: localPath,
  };

  const subPathStartIndex = {
    prod: appBasePath.PROD.length,
    local: appBasePath.LOCAL.length,
  };
  const { prod, local } = subPathStartIndex;
  const subPathIndex = process.env.NODE_ENV === 'production' ? prod : local;
  return pathname.substring(subPathIndex);
};

export const getSubPathsForBoka = (subPath: typeof SUB_PATH): string[] => {
  if (!isBrowser()) return [];
  return getBokaSubPathname(window.location.pathname, subPath)
    .split('/')
    .filter((path) => path !== PLAIN_VIEW && path !== NATIVE_APP);
};

export const isClientEnteringRootPath = (subPath: typeof SUB_PATH) => {
  if (!isBrowser()) return false;
  const subPaths = getSubPathsForBoka(subPath);
  return !subPaths[0] || !subPaths[1];
};

export const isClientEnteringOperationPath = (
  operationPath: OperationPath,
  subPath: typeof SUB_PATH,
) => {
  if (!isBrowser()) return false;
  const subPaths = getSubPathsForBoka(subPath);
  return subPaths[1] === operationPath;
};

export const hasOperationChanged = (
  operation: string,
  subPath: typeof SUB_PATH,
) => {
  if (!isBrowser()) return false;
  const subPaths = getSubPathsForBoka(subPath);
  return !!subPaths[0] && subPaths[0] !== operation;
};

const BASE_BUSINESS_OPERATIONS = [
  'athomeservices',
  'b2bkitchenfollowup',
  'benefit_free_space_consultation',
  'benefit_free_space_consultation_remote',
  'benefit_space_solution',
  'business',
  'business_id_service',
  'businessadvanced',
  'businessall',
  'businessdesign',
  'businessdesignbasic',
  'businessdesignconsultation',
  'businessdesignpremium',
  'businessextra',
  'businesshome',
  'businessinteriordesign',
  'businessnetwork',
  'businessremote',
  'businesstips',
  'businesstore',
  'coworkerbookedservicesbusiness',
  'coworkingbusiness',
  'designcheck',
  'designconsultingbusiness',
  'designservicebusiness',
  'externalbusiness',
  'ibn',
  'ibnconsultation',
  'ibnextra',
  'initialmeetingbusiness',
  'kitchenbusiness',
  'onlinebusiness',
  'remotebusiness',
  'servicesb2b',
  'storebusiness',
];

const BASE_OPERATIONS = [
  NATIVE_APP_OPERATION,
  BOKA_TEST_SERVICE_OPERATION,
  TIMESLOTSTODAY,
  'advice',
  'aftersales',
  'assistedshopping',
  'bathroom',
  'bathroomextra',
  'bathroomstore',
  'bedroom',
  'bedroomextra',
  'besta',
  'birthday',
  'buyback',
  'callback',
  'childrensroom',
  'childroom',
  'cleanenergy',
  'completedesign',
  'consultation',
  'consulting',
  'consultingdesign',
  'coworkerbookedservices',
  'coworkerbookedserviceskitchen',
  'design',
  'designbasic',
  'designcomplete',
  'designconsultation',
  'designcustomer',
  'designextra',
  'designhome',
  'designpremium',
  'designproject',
  'designremote',
  'designstore',
  'energyservices',
  'expertcheck',
  'event',
  'finalization',
  'finance',
  'fleamarket',
  'flex',
  'funday',
  'gongi',
  'hallway',
  'homecoach',
  'homedesign',
  'homeplanning',
  'homesmart',
  'ifpricetest',
  'individual',
  'initialmeeting',
  'instorebedroomextra',
  'instorelivingroom',
  'instorewardrobe',
  'interiordesign',
  'interiordesignremote',
  'interiordesignvalidation',
  'internal',
  'internalservices',
  'internaluseonly',
  'kitchen',
  'kitchen_eec_dda_aed_bdf',
  'kitchen_sf_trial',
  'kitchenadditional',
  'kitchencheck',
  'kitchenconsultation',
  'kitchenexpertcheck',
  'kitchenextra',
  'kitchenfinalizeremote',
  'kitchenfinalizestore',
  'kitchenfirst',
  'kitchenfollowup',
  'kitcheninhome',
  'kitcheninternal',
  'kitchenmeasurement',
  'kitchenmeasurementplanning',
  'kitchenonline',
  'kitchenparts',
  'kitchenplan',
  'kitchenplanning',
  'kitchenplanningall',
  'kitchenremote',
  'kitchenrenewal',
  'kitchenstorage',
  'kitchenstore',
  'lastare',
  'livingroom',
  'livingroomcomplete',
  'livingroomextra',
  'mattress',
  'measure',
  'measure_experiment', // test operation area for RS market
  'measurea',
  'measureb',
  'organize',
  'pax',
  'paxvalidation',
  'planningathome',
  'rcmp',
  'recycle',
  'remotelivingroom',
  'remotemeasurement',
  'remoteservices',
  'remotesofa',
  'remotewardrobe',
  'reservation',
  'seasonal',
  'service',
  'servicedesign',
  'shopper',
  'signlanguagesupport',
  'sleep',
  'sleepevent',
  'smarthome',
  'sofa',
  'storage',
  'storechecks',
  'testservice',
  'tidsamm',
  'trailer',
  'transportservices',
  'upcomingwaves',
  'verification',
  'wardrobe',
  'workspace',
  'worktopplanning',
];

const VALID_PAGES = ['questions', 'service-unavailable', 'questions-confirmed'];

/* Information about the query parameters used in this appplication (both EXTERNAL and BOKA) can be found here:
https://confluence.build.ingka.ikea.com/pages/viewpage.action?pageId=817044873&spaceKey=MSS&title=Customer%2Bquery%2Bparameters
*/
const EXTERNAL_QUERY_PARAMS = {
  VPC: 'vpc',
  PROJECT_ID: 'projectid',
  IMAGINATION: 'imagination',
  COOKIE_CONSENT_OVERRIDE: 'cookieconsent-override',
};

export const BOKA_QUERY_PARAMS = {
  TOKEN: 'token',
  ZIP: 'zip',
  SERVICE: 'service',
  OPERATION: 'operation',
  STORE: 'store',
  REFAPP: 'ref_app',
  EVENT_ACTION: 'event_action',
  STORESELECTOR: 'storeselector',
  TRANSLATIONSKEYS: 'translationskeys',
  TEST_MODE: 'test_mode',
};

const isPageNameValid = (page: string): boolean => {
  return VALID_PAGES.includes(page);
};

export const isBusinessOperation = (
  operation: string,
  appConfig: typeof CSS_CONFIG,
): boolean => {
  const toNormalOperations = appConfig.CONVERTTONORMALOPERATIONS;
  const businessOperations = BASE_BUSINESS_OPERATIONS.filter(
    (businessOperation) => !toNormalOperations.includes(businessOperation),
  );
  return businessOperations.includes(operation);
};

const isValidBokaOperation = (
  operation: string,
  appConfig: typeof CSS_CONFIG,
): boolean => {
  const operations = [
    ...BASE_OPERATIONS,
    ...appConfig.CONVERTTONORMALOPERATIONS,
  ];
  return (
    operations.includes(operation) || isBusinessOperation(operation, appConfig)
  );
};

const isValidPageRequest = (
  appConfig: typeof CSS_CONFIG,
  subPath: typeof SUB_PATH,
  zip?: string,
): boolean => {
  const [page, operationSubPage] = getSubPathsForBoka(subPath);
  if (page.length === 0) return true;
  if (isPageNameValid(page) || isPageNameValid(operationSubPage)) return true;

  const isValidOperationPage = isValidBokaOperation(page, appConfig);
  if (isValidOperationPage && operationSubPage && !zip) return false;

  return isValidOperationPage;
};

export const shouldRedirect = (
  appConfig: typeof CSS_CONFIG,
  subPath: typeof SUB_PATH,
  zip?: string,
): boolean => {
  return !isValidPageRequest(appConfig, subPath, zip);
};

interface ParsedQueryParams extends NativeAppParsedQueryParams {
  questionsToken: string;
  zip: string;
  service: string;
  vpc: string;
  projectId: string;
  imagination: string;
  eventAction: string;
  operation: string;
  store: string;
  refApp: string;
  storeSelector: boolean;
  translationsKeys: boolean;
}

const ALPHA_NUMERIC_DASH_UNDERSCORE_REGEX = /[^a-zA-Z0-9_-]/g;

export const sanitizeInput = (param: string): string => {
  return param.replace(ALPHA_NUMERIC_DASH_UNDERSCORE_REGEX, '');
};

export const getQueryParamsFromURL = (): ParsedQueryParams => {
  const {
    SERVICE,
    ZIP,
    TOKEN,
    OPERATION,
    STORE,
    REFAPP,
    EVENT_ACTION,
    STORESELECTOR,
    TRANSLATIONSKEYS,
  } = BOKA_QUERY_PARAMS;
  const { VPC, PROJECT_ID, IMAGINATION } = EXTERNAL_QUERY_PARAMS;
  const queryString = isBrowser() ? window.location.search : '';
  const queryParams = new URLSearchParams(queryString);

  const questionsToken = queryParams.get(TOKEN) || '';
  const zip = queryParams.get(ZIP) || '';
  const service = queryParams.get(SERVICE) || '';
  const vpc = sanitizeInput(queryParams.get(VPC) || '');
  const projectId = sanitizeInput(queryParams.get(PROJECT_ID) || '');
  const imagination = sanitizeInput(queryParams.get(IMAGINATION) || '');
  const eventAction = sanitizeInput(queryParams.get(EVENT_ACTION) || '');
  const operation = queryParams.get(OPERATION) || '';
  const store = queryParams.get(STORE) || '';
  const refApp = sanitizeInput(queryParams.get(REFAPP) || '');
  const storeSelector =
    queryParams.get(STORESELECTOR) === 'false' ? false : true;
  const translationsKeys =
    queryParams.get(TRANSLATIONSKEYS) === 'true' ? true : false;
  const nativeAppParams = getNativeAppQueryParams();

  return {
    questionsToken,
    zip,
    service,
    vpc,
    projectId,
    imagination,
    eventAction,
    operation,
    store,
    refApp,
    storeSelector,
    translationsKeys,
    ...nativeAppParams,
  };
};

type PersistingQueryParamKeys =
  | 'vpc'
  | 'projectid'
  | 'imagination'
  | 'ref_app'
  | 'event_action'
  | 'cookieconsent-override'
  | 'storeselector'
  | 'translationskeys'
  | 'test_mode'
  | NativeAppQueryParamKeys;

const filterQueryParams = (
  queryParams: URLSearchParams,
  exclude: PersistingQueryParamKeys[],
) => {
  const keys = [...queryParams.keys()];
  const persistingQueryParams = [
    ...Object.values(EXTERNAL_QUERY_PARAMS),
    BOKA_QUERY_PARAMS.REFAPP,
    BOKA_QUERY_PARAMS.EVENT_ACTION,
    BOKA_QUERY_PARAMS.STORESELECTOR,
    BOKA_QUERY_PARAMS.TRANSLATIONSKEYS,
    BOKA_QUERY_PARAMS.TEST_MODE,
    ...getNativeAppQueryParamNames(),
  ];

  keys.forEach((key) => {
    if (
      !persistingQueryParams.includes(key) ||
      (exclude as string[]).includes(key)
    ) {
      queryParams.delete(key);
    }
  });
};

type AddQueryParamsToPathOptions = {
  addToExistingQueryParam?: boolean;
  exclude?: PersistingQueryParamKeys[];
};

export const addPersistingQueryParamsToPath = (
  path: string,
  options?: AddQueryParamsToPathOptions,
): string => {
  if (!isBrowser()) {
    return path;
  }

  const { addToExistingQueryParam = false, exclude = [] } = options || {};
  const queryParams = new URLSearchParams(window.location.search);
  filterQueryParams(queryParams, exclude);
  const queryParamsString = queryParams.toString();

  return queryParamsString
    ? `${path}${addToExistingQueryParam ? '&' : '?'}${queryParamsString}`
    : path;
};

export const getPrevPath = (operation: string): string => {
  const currentUrl = window.location.pathname;

  let prevPage = '';
  if (currentUrl.endsWith('/contact/')) {
    prevPage = 'timeslots/';
  } else if (currentUrl.endsWith('/timeslots/')) {
    prevPage = 'services/';
  } else if (currentUrl.endsWith('/services/')) {
    prevPage = '';
  }

  let path = '';
  if (isPlainView()) {
    path = `/${PLAIN_VIEW}/${operation}/${prevPage}`;
  } else if (isNativeAppView()) {
    path = `/${NATIVE_APP}/${operation}/${prevPage}`;
  } else {
    path = `/${operation}/${prevPage}`;
  }
  return path;
};

export const getPath = (operation: string, step: string): string => {
  if (isPlainView()) {
    return `/${PLAIN_VIEW}/${operation}/${step}/`;
  } else if (isNativeAppView()) {
    return `/${NATIVE_APP}/${operation}/${step}/`;
  } else {
    return `/${operation}/${step}/`;
  }
};

/* ikeaapp cleanup: Remove NATIVE_APP_OPERATION once the app team starts using /app instead of /ikeaapp */
export const getPagePath = (page: string): string => {
  if (isPlainView()) {
    return `/${PLAIN_VIEW}/${page}/`;
  } else if (isNativeAppOperation()) {
    return `/${NATIVE_APP_OPERATION}/${page}/`;
  } else if (isNativeAppView()) {
    return `/${NATIVE_APP}/${page}/`;
  } else {
    return `/${page}/`;
  }
};
